import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Mail, Lock, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function AuthModal() {
  const { authOpen, setAuthOpen, login, register, loginWithGoogle, user, cancelPendingCheckout } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (authOpen) setMode("login");
  }, [authOpen]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Signed in");
      } else {
        await register(email, password, name);
        toast.success("Account created");
      }
      setAuthOpen(false);
      setEmail(""); setPassword(""); setName("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full bg-[#151515] border border-zinc-800 pl-10 pr-3 py-3 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-volt font-mono";

  return (
    <Dialog
      open={authOpen}
      onOpenChange={(open) => {
        setAuthOpen(open);
        if (!open && !user) cancelPendingCheckout();
      }}
    >
      <DialogContent className="rounded-none bg-[#0b0b0b] border border-zinc-800 text-white max-w-md p-0 gap-0 font-mono max-h-[92vh] overflow-y-auto">
        <div className="p-6 sm:p-8">
          <DialogTitle className="font-display text-3xl tracking-tight mb-1 font-normal">
            {mode === "login" ? "SIGN IN" : "CREATE ACCOUNT"}
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500 tracking-wide mb-6">
            Unlock your free character quota — upgrade anytime for unlimited speech.
          </DialogDescription>

          <button
            type="button"
            onClick={loginWithGoogle}
            data-testid="google-signin-btn"
            className="w-full flex items-center justify-center gap-3 bg-white text-black py-3 text-sm font-bold uppercase tracking-widest hover:bg-zinc-200 transition-colors duration-100 mb-4"
          >
            <svg width="18" height="18" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-5">
            <span className="h-px bg-zinc-800 flex-grow" />
            <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-600">or email</span>
            <span className="h-px bg-zinc-800 flex-grow" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input
                  className={inputCls}
                  placeholder="Name"
                  aria-label="Name"
                  name="name"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  data-testid="auth-name-input"
                />
              </div>
            )}
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input
                type="email"
                className={inputCls}
                placeholder="Email"
                aria-label="Email"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="auth-email-input"
              />
            </div>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input
                type="password"
                className={inputCls}
                placeholder="Password"
                aria-label="Password"
                name="password"
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                maxLength={72}
                data-testid="auth-password-input"
              />
            </div>
            <button
              type="button"
              type="submit"
              disabled={busy}
              data-testid="auth-submit-btn"
              className="w-full flex items-center justify-center gap-2 bg-volt text-black py-3 text-sm font-bold uppercase tracking-widest hover:bg-volt-hover transition-colors duration-100 disabled:opacity-60"
            >
              {busy && <Loader2 size={16} className="animate-spin" />}
              {mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <p className="text-xs text-zinc-500 mt-5 text-center">
            {mode === "login" ? "No account yet?" : "Already have an account?"}{" "}
            <button
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-volt hover:underline"
              data-testid="auth-toggle-mode"
            >
              {mode === "login" ? "Create one" : "Sign in"}
            </button>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
